/**
 * The search box above every element table, and the same one on all of them.
 *
 * There used to be two shapes: a five-menu faceted bar on requirements and a
 * plain box everywhere else, with MUI's per-column filter menus sitting under
 * both because nobody had turned them off. Three mechanisms, two of them
 * unchosen. Now: one box here, column filters in the grid, and nothing else.
 *
 * The bar holds no state of its own - it is given the query and reports a
 * change - because the query lives in the URL, so a filtered list is a LINK
 * somebody pastes into an issue and a reload lands back on the same question.
 * The one exception is the keystrokes themselves, which the caller owns; see
 * the screens.
 */

import ClearIcon from '@mui/icons-material/Clear'
import IconButton from '@mui/material/IconButton'
import InputAdornment from '@mui/material/InputAdornment'
import SearchIcon from '@mui/icons-material/Search'
import TextField from '@mui/material/TextField'

export interface EntityFilterBarProps {
  /** The plural label of what is being filtered, for the placeholder. */
  plural: string
  value: string
  onChange: (next: string) => void
  /**
   * Extra clause for the placeholder, for a screen that does something a table
   * does not.
   *
   * The tree screens keep the ANCESTORS of a match so a hit is not shown
   * orphaned from its context. That is real behaviour a person needs told, and
   * it is the reason those placeholders were all worded differently in the
   * first place. Offering one slot for it keeps every bar the same sentence
   * with one optional ending, rather than six sentences.
   */
  hint?: string
}

export default function EntityFilterBar({
  plural,
  value,
  onChange,
  hint,
}: EntityFilterBarProps) {
  return (
    <TextField
      value={value}
      onChange={(event) => onChange(event.target.value)}
      // One wording for every table. It says what the box does - matches any
      // column - rather than naming three fields, because naming fields is how
      // the two old placeholders fell behind the registry.
      placeholder={`Filter ${plural} — matches any column${hint ? `, ${hint}` : ''}`}
      size="small"
      fullWidth
      // `slotProps`, not the v5 `InputProps`/`inputProps`: MUI 9 removed those
      // and the compiler is the only thing that says so.
      slotProps={{
        // Labelled for the accessibility tree as well as the eye: the tests find
        // this box by role and name, and so does anyone using a screen reader.
        htmlInput: { 'aria-label': `Filter ${plural}` },
        input: {
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" color="disabled" />
            </InputAdornment>
          ),
          // Offered only when there is something to clear, so the control is not
          // a permanent piece of furniture that does nothing.
          endAdornment: value ? (
            <InputAdornment position="end">
              <IconButton size="small" aria-label="Clear filter" onClick={() => onChange('')}>
                <ClearIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ) : null,
        },
      }}
    />
  )
}
